import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Loader2, Mail, CheckCircle, ChevronDown, UserCircle2, ArrowLeft } from 'lucide-react';
import api from '../api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DEFAULT_AVATARS } from '../constants/avatars';

import { logoV1 as LOGO } from '../assets';

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const inp = "w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-[#004B00] focus:ring-2 focus:ring-[#004B00]/20 outline-none font-nunito text-sm transition-all bg-white text-gray-900";
const lbl = "block text-sm font-medium text-gray-700 mb-1 font-nunito";

export default function AuthPage() {
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [verifyData, setVerifyData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const { login, verifyTotpLogin, setUserFromToken } = useAuth();
  const navigate = useNavigate();
  const [lf, setLf] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [totpChallenge, setTotpChallenge] = useState(null); // { token, expiresIn }
  const [totpCode, setTotpCode] = useState('');
  const [rf, setRf] = useState({ email: '', password: '', first_name: '', last_name: '', city: '', state: '', zip_code: '', phone: '', profile_photo: '', beta_code: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [tosScrolled, setTosScrolled] = useState(false);
  const tosRef = useRef(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const lookupZip = async (zip) => {
    if (zip.length !== 5 || !/^\d{5}$/.test(zip)) return;
    try {
      const res = await api.get(`/utils/zip-info?zip=${zip}`);
      if (res.data.found) {
        setRf(f => ({
          ...f,
          state: res.data.state,
          city: f.city.trim() === '' ? res.data.city : f.city,
        }));
      }
    } catch { /* silent */ }
  };

  const handleZipChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setRf(f => ({ ...f, zip_code: val }));
    if (val.length === 5) lookupZip(val);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await login(lf.email, lf.password, rememberMe);
      if (res?.requires_totp) {
        setTotpChallenge({ token: res.challenge_token, expiresIn: res.challenge_expires_in });
        toast.info('Enter the 6-digit code from your authenticator app.');
      } else {
        navigate('/feed');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === 'object' && detail?.user_id) {
        setVerifyData({ user_id: detail.user_id, email: lf.email });
        toast.info('Please verify your email to continue.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Login failed');
      }
    }
    finally { setLoading(false); }
  };

  const handleTotpVerify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await verifyTotpLogin(totpChallenge.token, totpCode.trim(), rememberMe);
      toast.success('Welcome back!');
      navigate('/feed');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : '2FA verification failed');
      if (err.response?.status === 401 && typeof detail === 'string' && detail.toLowerCase().includes('expired')) {
        setTotpChallenge(null); setTotpCode('');
      }
    }
    finally { setLoading(false); }
  };

  const handleTosScroll = () => {
    const el = tosRef.current;
    if (!el || tosScrolled) return;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 24) setTosScrolled(true);
  };

  const passwordsMatch = !confirmPassword || rf.password === confirmPassword;
  const passwordMatchError = confirmPassword && rf.password !== confirmPassword;

  const handleRegister = async (e) => {
    e.preventDefault();
    if (rf.password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', rf);
      if (res.data.needs_verification) {
        setVerifyData({ user_id: res.data.user_id, email: res.data.email });
        toast.success('Account created! Check your email for the verification code.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/auth/verify-email', { user_id: verifyData.user_id, code: verifyCode });
      setUserFromToken(res.data.access_token, res.data.user);
      toast.success('Email verified! Welcome to Neighborli!');
      navigate('/feed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    try {
      await api.post('/auth/resend-verification', { user_id: verifyData.user_id });
      toast.success('New verification code sent to your email!');
    } catch { toast.error('Failed to resend'); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch {
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setLoading(false);
    }
  };

  const Branding = () => (
    <div className="hidden lg:flex lg:w-[45%] bg-[#004B00] flex-col items-center justify-center p-12 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5" style={{backgroundImage:'radial-gradient(circle,white 1.5px,transparent 1.5px)',backgroundSize:'28px 28px'}}/>
      <div className="relative z-10 text-center max-w-sm">
        <div className="mb-8">
          <div className="bg-white rounded-2xl p-5 shadow-xl inline-block">
            <img src={LOGO} alt="Neighborli" className="h-36 w-auto" />
          </div>
        </div>
        <h2 className="text-white text-2xl font-montserrat font-bold mb-3">The Modern Way<br/>to Share.</h2>
        <p className="text-white/70 font-opensans mb-10">Borrow tools, share resources, and connect with neighbors.</p>
        <div className="grid grid-cols-3 gap-4 text-center mb-10">
          {[['Share','Lend what you own'],['Borrow','Get what you need'],['Connect','Build community']].map(([t,d])=>(
            <div key={t} className="text-white">
              <div className="font-montserrat font-semibold">{t}</div>
              <div className="text-white/50 text-xs mt-1 font-opensans">{d}</div>
            </div>
          ))}
        </div>
        
      </div>
    </div>
  );

  // Forgot password screens
  if (forgotMode) {
    return (
      <div className="min-h-screen flex bg-[#F9F9F9]">
        <Branding />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 text-center">
              {forgotSent ? (
                <>
                  <div className="w-16 h-16 bg-[#004B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail size={28} className="text-[#004B00]" />
                  </div>
                  <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-2">Check your email</h2>
                  <p className="text-gray-500 font-nunito text-sm mb-6">
                    If <strong className="text-gray-800">{forgotEmail}</strong> is registered, we've sent a password reset link. It expires in 1 hour.
                  </p>
                  <button
                    onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(''); }}
                    className="w-full border border-stone-200 text-gray-700 rounded-xl py-3 font-semibold font-opensans hover:bg-stone-50 transition-all flex items-center justify-center gap-2"
                    data-testid="back-to-signin"
                  >
                    <ArrowLeft size={16} /> Back to Sign In
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-[#004B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail size={28} className="text-[#004B00]" />
                  </div>
                  <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-2">Forgot your password?</h2>
                  <p className="text-gray-500 font-nunito text-sm mb-6">
                    Enter your email address and we'll send you a link to reset it.
                  </p>
                  <form onSubmit={handleForgotPassword} className="space-y-4 text-left" data-testid="forgot-password-form">
                    <div>
                      <label className={lbl}>Email address</label>
                      <input
                        data-testid="forgot-email-input"
                        type="email"
                        required
                        className={inp}
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoFocus
                      />
                    </div>
                    <button
                      data-testid="forgot-submit"
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#FF5500] hover:bg-[#e04b00] text-white rounded-xl py-3 font-semibold font-opensans transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                      Send Reset Link
                    </button>
                  </form>
                  <button
                    onClick={() => setForgotMode(false)}
                    className="mt-4 text-sm text-gray-500 font-nunito hover:text-gray-700 flex items-center gap-1 mx-auto"
                    data-testid="back-to-signin-link"
                  >
                    <ArrowLeft size={14} /> Back to Sign In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2FA challenge screen (after successful password auth)
  if (totpChallenge) {
    return (
      <div className="min-h-screen flex bg-[#F9F9F9]">
        <Branding />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 text-center" data-testid="user-totp-challenge">
              <div className="w-16 h-16 bg-[#004B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCircle2 size={28} className="text-[#004B00]"/>
              </div>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-2">Two-factor authentication</h2>
              <p className="text-gray-500 font-nunito text-sm mb-6">
                Enter the 6-digit code from your authenticator app, or use a recovery code.
              </p>
              <form onSubmit={handleTotpVerify} className="space-y-4 text-left">
                <div>
                  <label className={lbl}>Authentication code</label>
                  <input
                    data-testid="user-totp-login-code"
                    required minLength={6} maxLength={20}
                    className={`${inp} text-center text-2xl tracking-[0.3em] font-poppins`}
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.slice(0, 20))}
                    placeholder="123456 or XXXX-XXXX"
                    autoFocus
                    inputMode="text"
                  />
                  <p className="text-xs text-gray-400 font-nunito mt-1.5 text-center">Code expires shortly — try again if it doesn't work.</p>
                </div>
                <button data-testid="user-totp-login-submit" type="submit" disabled={loading || totpCode.length < 6}
                  className="w-full bg-[#FF5500] hover:bg-[#e04b00] text-white rounded-xl py-3 font-semibold font-opensans transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? <Loader2 size={18} className="animate-spin"/> : <CheckCircle size={18}/>} Verify & Sign in
                </button>
              </form>
              <div className="mt-4 flex items-center justify-center text-sm">
                <button onClick={() => { setTotpChallenge(null); setTotpCode(''); }} className="text-gray-500 font-nunito hover:underline">Back to sign in</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Email verification screen
  if (verifyData) {
    return (
      <div className="min-h-screen flex bg-[#F9F9F9]">
        <Branding />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 text-center">
              <div className="w-16 h-16 bg-[#004B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={28} className="text-[#004B00]"/>
              </div>
              <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-2">Check your email</h2>
              <p className="text-gray-500 font-nunito text-sm mb-6">
                We sent a 6-digit verification code to<br/><strong className="text-gray-800">{verifyData.email}</strong>
              </p>
              <form onSubmit={handleVerify} className="space-y-4 text-left">
                <div>
                  <label className={lbl}>Enter verification code</label>
                  <input
                    data-testid="verify-code-input"
                    required maxLength={6} minLength={6}
                    className={`${inp} text-center text-2xl tracking-[0.4em] font-poppins`}
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    autoFocus
                  />
                  <p className="text-xs text-gray-400 font-nunito mt-1.5 text-center">The code expires in 10 minutes</p>
                </div>
                <button data-testid="verify-submit" type="submit" disabled={loading || verifyCode.length !== 6}
                  className="w-full bg-[#FF5500] hover:bg-[#e04b00] text-white rounded-xl py-3 font-semibold font-opensans transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? <Loader2 size={18} className="animate-spin"/> : <CheckCircle size={18}/>} Verify & Continue
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-sm">
                <button onClick={handleResend} className="text-[#004B00] font-semibold font-nunito hover:underline">Resend code</button>
                <button onClick={() => { setVerifyData(null); setVerifyCode(''); }} className="text-gray-500 font-nunito hover:underline">Back to sign in</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F9F9]">
      <div className="flex flex-1">
        <Branding />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO} alt="Neighborli" className="h-24 mx-auto" />
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8">
            <div className="flex bg-stone-100 rounded-xl p-1 mb-6">
              {['login','register'].map(t=>(
                <button key={t} onClick={()=>setTab(t)} data-testid={`auth-tab-${t}`}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold font-nunito transition-all ${tab===t?'bg-white text-[#004B00] shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
                  {t==='login'?'Sign In':'Join Neighborli'}
                </button>
              ))}
            </div>

            {tab==='login' ? (
              <form onSubmit={handleLogin} data-testid="login-form">
                <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-6">Welcome back</h2>
                <div className="space-y-4">
                  <div>
                    <label className={lbl}>Email</label>
                    <input data-testid="login-email" type="email" required className={inp} value={lf.email} onChange={e=>setLf({...lf,email:e.target.value})} placeholder="you@example.com"/>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className={lbl} style={{marginBottom:0}}>Password</label>
                      <button
                        type="button"
                        onClick={() => { setForgotMode(true); setForgotEmail(lf.email); }}
                        className="text-xs text-[#004B00] font-nunito font-semibold hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <input data-testid="login-password" type={showPass?'text':'password'} required className={inp} value={lf.password} onChange={e=>setLf({...lf,password:e.target.value})} placeholder="••••••••"/>
                      <button type="button" onClick={()=>setShowPass(!showPass)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                        {showPass?<EyeOff size={18}/>:<Eye size={18}/>}
                      </button>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 font-nunito">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      data-testid="stay-logged-in-checkbox"
                      className="w-4 h-4 accent-[#004B00] cursor-pointer"
                    />
                    Stay logged in on this device
                  </label>
                  <button data-testid="login-submit" type="submit" disabled={loading}
                    className="w-full bg-[#FF5500] hover:bg-[#e04b00] text-white rounded-xl py-3 font-semibold font-opensans transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading&&<Loader2 size={18} className="animate-spin"/>} Sign In
                  </button>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4 font-nunito">
                  No account? <button type="button" onClick={()=>setTab('register')} className="text-[#004B00] font-semibold">Join now</button>
                </p>
                <p className="text-center text-sm text-gray-500 mt-1 font-nunito">
                  No beta code? <a href="/early-access" className="text-[#FF5500] font-semibold hover:underline">Join the waitlist →</a>
                </p>
              </form>
            ) : (
              <form onSubmit={handleRegister} data-testid="register-form">
                <h2 className="text-2xl font-poppins font-semibold text-gray-900 mb-5">Create your account</h2>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>First Name</label>
                      <input data-testid="register-first-name" required className={inp} value={rf.first_name} onChange={e=>setRf({...rf,first_name:e.target.value})} placeholder="Jane"/>
                    </div>
                    <div>
                      <label className={lbl}>Last Name</label>
                      <input data-testid="register-last-name" required className={inp} value={rf.last_name} onChange={e=>setRf({...rf,last_name:e.target.value})} placeholder="Smith"/>
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Email</label>
                    <input data-testid="register-email" type="email" required className={inp} value={rf.email} onChange={e=>setRf({...rf,email:e.target.value})} placeholder="you@example.com"/>
                  </div>
                  <div>
                    <label className={lbl}>Password</label>
                    <div className="relative">
                      <input data-testid="register-password" type={showPass?'text':'password'} required minLength={6} className={inp} value={rf.password} onChange={e=>setRf({...rf,password:e.target.value})} placeholder="Min 6 characters"/>
                      <button type="button" onClick={()=>setShowPass(!showPass)} className="absolute right-3 top-2.5 text-gray-400"><Eye size={18}/></button>
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Confirm Password</label>
                    <div className="relative">
                      <input
                        data-testid="register-confirm-password"
                        type={showConfirm?'text':'password'}
                        required
                        minLength={6}
                        className={`${inp} ${passwordMatchError ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20' : confirmPassword && !passwordMatchError ? 'border-green-400 focus:border-green-400 focus:ring-green-400/20' : ''}`}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                      />
                      <div className="absolute right-3 top-2.5 flex items-center gap-1.5">
                        {confirmPassword && (
                          <span>
                            {passwordMatchError
                              ? <span className="text-red-500 text-xs font-semibold">✗</span>
                              : <CheckCircle size={16} className="text-green-500"/>}
                          </span>
                        )}
                        <button type="button" onClick={()=>setShowConfirm(!showConfirm)} className="text-gray-400 hover:text-gray-600">
                          {showConfirm ? <EyeOff size={18}/> : <Eye size={18}/>}
                        </button>
                      </div>
                    </div>
                    {passwordMatchError && (
                      <p className="text-xs text-red-500 font-opensans mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>City</label>
                      <input data-testid="register-city" required className={inp} value={rf.city} onChange={e=>setRf({...rf,city:e.target.value})} placeholder="Springfield"/>
                    </div>
                    <div>
                      <label className={lbl}>State</label>
                      <Select value={rf.state} onValueChange={val=>setRf({...rf,state:val})}>
                        <SelectTrigger data-testid="register-state" className={inp}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {STATES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={lbl}>Zip Code</label>
                      <input data-testid="register-zip" required className={inp} value={rf.zip_code} onChange={handleZipChange} placeholder="12345" maxLength={5}/>
                    </div>
                    <div>
                      <label className={lbl}>Phone</label>
                      <input data-testid="register-phone" required className={inp} value={rf.phone} onChange={e=>setRf({...rf,phone:e.target.value})} placeholder="503-123-4567"/>
                    </div>
                  </div>
                  <div>
                    <label className={`${lbl} flex items-center gap-1.5`}>
                      Beta Access Code <span className="text-[#FF5500] font-bold">*</span>
                    </label>
                    <input
                      data-testid="register-beta-code"
                      required
                      className={`${inp} font-mono tracking-widest uppercase`}
                      value={rf.beta_code}
                      onChange={e=>setRf({...rf, beta_code: e.target.value.toUpperCase()})}
                      placeholder="NL-XXXXXX"
                      maxLength={12}
                    />
                    <p className="text-xs text-gray-400 font-nunito mt-1">
                      Don't have a code?{' '}
                      <a href="/early-access" target="_blank" rel="noopener noreferrer" className="text-[#004B00] font-semibold hover:underline">
                        Join the waitlist →
                      </a>
                    </p>
                  </div>
                  {/* Terms of Service */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5 font-opensans">
                      Terms of Service
                      {!tosScrolled && (
                        <span className="ml-2 text-xs text-[#FF5500] font-normal">— scroll to the bottom to accept</span>
                      )}
                    </label>
                    <div
                      ref={tosRef}
                      onScroll={handleTosScroll}
                      data-testid="tos-scroll-box"
                      className="h-44 overflow-y-auto border border-stone-200 rounded-xl bg-stone-50 p-4 text-xs text-gray-600 font-opensans leading-relaxed space-y-3 scroll-smooth"
                    >
                      <p className="font-semibold text-gray-800">Terms of Service for Neighborli</p>
                      <p className="text-gray-500">Effective Date: February 28, 2026 · www.neighborli.net</p>
                      <p>Welcome to Neighborli. By accessing our website or using our application, you agree to be bound by these Terms of Service.</p>

                      <p className="font-semibold text-gray-800">1. The Nature of the Service</p>
                      <p>Neighborli is a peer-to-peer platform connecting Lenders with Borrowers. We are a venue only — we do not own, inspect, store, or deliver any items listed. Neighborli makes no representations regarding the safety, quality, or legality of shared items.</p>

                      <p className="font-semibold text-gray-800">2. User Accounts and Eligibility</p>
                      <p>You must be at least 18 years old to create an account. You agree to provide accurate information during registration and are responsible for maintaining the confidentiality of your credentials. Neighborli is intended for use within local communities where you reside or are physically present.</p>

                      <p className="font-semibold text-gray-800">3. Sharing Rules and Responsibilities</p>
                      <p><strong>Lenders</strong> must accurately describe items, including any defects or safety concerns, and must not share items that are broken, recalled, or dangerous. <strong>Borrowers</strong> agree to use items only for their intended purpose, to use them safely, and to return them in the same condition as received. Borrowers are responsible for providing their own consumables (e.g., fuel, sandpaper, drill bits) unless otherwise agreed.</p>

                      <p className="font-semibold text-gray-800">4. Fees and Advertising</p>
                      <p>Basic peer-to-peer sharing is free. Neighborli may display sponsored content from third-party advertisers governed by our Privacy Policy. Neighborli is not responsible for products or services offered by third-party advertisers.</p>

                      <p className="font-semibold text-gray-800">5. Limitation of Liability and Release</p>
                      <p>Neighborli is not a party to any agreement between users and has no control over the conduct of Lenders, Borrowers, or other users. By using Neighborli, you agree there is no agency, partnership, or employment relationship between you and Neighborli. You hereby waive, release, and forever discharge Neighborli from any liability arising from: (a) the physical condition or failure of a borrowed item; (b) any personal injury, death, or property damage from use of a borrowed item; (c) interactions or disputes between users, whether online or in person.</p>

                      <p className="font-semibold text-gray-800">6. Indemnification</p>
                      <p>You agree to indemnify and hold harmless Neighborli from all claims, damages, liabilities, costs, and expenses (including attorney's fees) arising from your use of the Service, any item you lend or borrow, your violation of these Terms, or your violation of any third-party right.</p>

                      <p className="font-semibold text-gray-800">7. Termination of Service</p>
                      <p>We reserve the right to suspend or terminate your account at our sole discretion, without notice, for conduct that violates these Terms or is harmful to the Neighborli community.</p>

                      <p className="font-semibold text-gray-800">8. Governing Law</p>
                      <p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Neighborli is headquartered, without regard to conflict of law principles.</p>
                    </div>
                    {!tosScrolled && (
                      <div className="flex items-center justify-center gap-1.5 mt-1.5 text-xs text-gray-400 font-opensans animate-bounce">
                        <ChevronDown size={14}/> Scroll down to read all terms
                      </div>
                    )}
                    <div className={`flex items-start gap-3 mt-3 transition-opacity ${tosScrolled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                      <input
                        id="tos-checkbox"
                        type="checkbox"
                        checked={tosAccepted}
                        onChange={e => tosScrolled && setTosAccepted(e.target.checked)}
                        disabled={!tosScrolled}
                        data-testid="tos-checkbox"
                        className="mt-0.5 w-4 h-4 accent-[#004B00] cursor-pointer flex-shrink-0"
                      />
                      <label htmlFor="tos-checkbox" className={`text-xs font-opensans leading-relaxed ${tosScrolled ? 'text-gray-700 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}>
                        I have read and agree to the{' '}
                        <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="tos-link"
                          className="text-[#004B00] font-semibold hover:underline">
                          Terms of Service
                        </Link>
                      </label>
                    </div>
                  </div>
                  <button data-testid="register-submit" type="submit" disabled={loading || !tosAccepted || passwordMatchError || !confirmPassword}
                    className="w-full bg-[#FF5500] hover:bg-[#e04b00] text-white rounded-xl py-3 font-semibold font-opensans transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2 mt-1">
                    {loading&&<Loader2 size={18} className="animate-spin"/>} Create Account
                  </button>
                </div>
                <p className="text-center text-sm text-gray-500 mt-4 font-nunito">
                  Have an account? <button type="button" onClick={()=>setTab('login')} className="text-[#004B00] font-semibold">Sign in</button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      </div>
      <div className="text-center py-4 text-xs text-gray-400 font-nunito border-t border-stone-200 bg-white">
        <Link to="/terms" className="hover:text-[#004B00] transition-colors">Terms of Service</Link>
        <span className="mx-2">·</span>
        <Link to="/privacy" className="hover:text-[#004B00] transition-colors">Privacy Policy</Link>
        <span className="mx-2">·</span>
        <span>© 2026 Neighborli</span>
      </div>
    </div>
  );
}
